import { renderUserRoleBadge } from '@/components/features/auth/auth'
import { DataTable } from '@/components/features/shared/DataTable'
import { Badge } from '@/components/ui/badge'
import { Button } from '@/components/ui/button'
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from '@/components/ui/dropdown-menu'
import { User, UserStatus } from '@/domain/auth/auth'
import { ColumnDef, PaginationState } from '@tanstack/react-table'
import { MoreHorizontal, Pencil, Trash2 } from 'lucide-react'

interface UserTableProps {
  users: User[]
  totalUsers: number
  pagination?: PaginationState
  onPaginationChange?: React.Dispatch<React.SetStateAction<PaginationState>>
  searchKey: string
  searchPlaceholder: string
  onCreateUser: () => void
  onEditUser: (user: User) => void
  onDeleteUser: (userId: string) => void
}

const statusColors: Record<UserStatus, string> = {
  [UserStatus.Active]: 'bg-emerald-100 text-emerald-800 hover:bg-emerald-100',
  [UserStatus.Inactive]: 'bg-gray-100 text-gray-800 hover:bg-gray-100',
  [UserStatus.Pending]: 'bg-yellow-100 text-yellow-800 hover:bg-yellow-100',
  [UserStatus.Suspended]: 'bg-red-100 text-red-800 hover:bg-red-100',
}

export const UserTable = ({
  users,
  totalUsers,
  pagination,
  onPaginationChange,
  searchKey,
  searchPlaceholder,
  onCreateUser,
  onEditUser,
  onDeleteUser,
}: UserTableProps) => {
  const columns: ColumnDef<User>[] = [
    {
      accessorKey: 'fullName',
      header: 'Nombre',
      cell: ({ row }) => {
        const user = row.original
        const name = user.fullName || user.email
        return <div className="font-medium">{name}</div>
      },
    },
    {
      accessorKey: 'email',
      header: 'Email',
      cell: ({ row }) => <div className="lowercase">{row.original.email}</div>,
    },
    {
      accessorKey: 'roles',
      header: 'Roles',
      cell: ({ row }) => <div className="flex flex-wrap gap-1">{row.original.roles.map((role) => renderUserRoleBadge(role))}</div>,
    },
    {
      accessorKey: 'status',
      header: 'Estado',
      cell: ({ row }) => {
        const status = row.original.status
        const labels: Record<UserStatus, string> = {
          [UserStatus.Active]: 'Activo',
          [UserStatus.Inactive]: 'Inactivo',
          [UserStatus.Pending]: 'Pendiente',
          [UserStatus.Suspended]: 'Suspendido',
        }
        return (
          <Badge className={statusColors[status]}>
            {labels[status]}
          </Badge>
        )
      },
    },
    {
      accessorKey: 'createdAt',
      header: 'Creado',
      cell: ({ row }) => {
        const date = new Date(row.original.createdAt)
        return <div className="text-sm text-muted-foreground">{date.toLocaleDateString('es-AR')}</div>
      },
    },
    {
      id: 'actions',
      enableHiding: false,
      cell: ({ row }) => {
        const user = row.original
        return (
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="h-8 w-8 p-0">
                <span className="sr-only">Menu</span>
                <MoreHorizontal className="h-4 w-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem onClick={() => onEditUser(user)}>
                <Pencil className="mr-2 h-4 w-4" />
                Editar
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => onDeleteUser(user.id)} className="text-destructive focus:text-destructive">
                <Trash2 className="mr-2 h-4 w-4" />
                Eliminar
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        )
      },
    },
  ]

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-xl font-semibold">Usuarios del Sistema</h2>
          <p className="text-sm text-muted-foreground">
            Usuarios que acceden al panel de administración
          </p>
        </div>
        <Button onClick={onCreateUser}>Nuevo Usuario</Button>
      </div>
      <DataTable
        columns={columns}
        data={users}
        searchKey={searchKey}
        searchPlaceholder={searchPlaceholder}
        pageCount={Math.ceil(totalUsers / (pagination?.pageSize ?? 10))}
        pagination={pagination}
        onPaginationChange={onPaginationChange}
        totalRecords={totalUsers}
      />
    </div>
  )
}
